/**
 * Overlay de tradução da Modeloja.
 *
 * O `docs/CUSTOMIZATION.md` autoriza duas formas de mexer em texto: criar chave nova nos
 * arquivos `en`, ou **sobrepor** os locales do upstream com um overlay que faz deep-merge.
 * Este é o overlay. Ele existe porque a alternativa — editar
 * `i18n/locale/pt_BR/*.json` na mão — cria conflito em TODO sync de tradução do upstream
 * (o Crowdin reescreve aqueles arquivos inteiros, sem dó), e o texto é justamente a camada
 * que mais vamos querer ajustar.
 *
 * Regra de uso: só entra aqui texto que o upstream já tem e que queremos DIFERENTE. Texto
 * de tela nossa continua morando no componente (o CRM é pt-BR travado) ou em chave nova.
 *
 * O merge é profundo e não destrutivo: chave que não está aqui continua vindo do upstream,
 * então uma chave que o upstream renomeie simplesmente para de ser sobreposta — degrada
 * para o texto original, nunca para tela vazia.
 */
import ptBR from './pt_BR.json';

const OVERRIDES = { pt_BR: ptBR };

const isPlainObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Aplica `source` sobre `target` recursivamente, devolvendo um objeto novo.
 * Não muta os locales do upstream — eles são módulos importados e compartilhados.
 */
export const deepMerge = (target, source) => {
  if (!isPlainObject(target)) return source;
  if (!isPlainObject(source)) return target;

  const result = { ...target };

  Object.keys(source).forEach(key => {
    result[key] =
      isPlainObject(result[key]) && isPlainObject(source[key])
        ? deepMerge(result[key], source[key])
        : source[key];
  });

  return result;
};

/**
 * Recebe o mapa de locales do upstream e devolve um mapa novo com os nossos textos por cima.
 * Locale sem override passa intacto (mesma referência), então o custo é zero para os 40+
 * idiomas que não tocamos.
 */
export const applyOverrides = messages =>
  Object.keys(OVERRIDES).reduce(
    (acc, locale) =>
      acc[locale]
        ? { ...acc, [locale]: deepMerge(acc[locale], OVERRIDES[locale]) }
        : acc,
    messages
  );

export default applyOverrides;
